import clsx from 'clsx';
import React from 'react';
import classes from './Card.module.scss';

/**
 * The generic "C" allows the root element to be overridden via the "component"
 * prop (e.g. <Card component={A}>). When omitted, the component defaults
 * to "section". The "Omit" ensures that any HTML attributes passed through
 * are typed correctly for whichever element is actually rendered.
 */
export type Props<C extends React.ElementType = 'section'> = {
  component?: C;
  isLink?: boolean;
  noBorderRadius?: boolean;
} & Omit<React.ComponentPropsWithoutRef<C>, 'component'>;

const Card = React.forwardRef(function Card<C extends React.ElementType = 'section'>(
  { className, children, isLink, noBorderRadius, component, ...rest }: Props<C>,
  ref: React.Ref<Element>,
) {
  const Root = (component || 'section') as React.ElementType;

  return (
    <Root
      {...rest}
      className={clsx(
        classes.root,
        className,
        isLink && classes.isLink,
        noBorderRadius && classes.noBorderRadius,
      )}
      ref={ref}
    >
      {children}
    </Root>
  );
}) as <C extends React.ElementType = 'section'>(
  props: Props<C> & { ref?: React.Ref<Element> },
) => React.ReactElement;

export default Card;
